import { Command } from './Command';
import { VideoEditor } from '../VideoEditor';
import { Track } from '../models/Track';
import { EditorChangeType } from '../types';
import { Element } from '../models/Element';

export class RemoveTrackCommand implements Command {
  private editor: VideoEditor;
  private trackId: string;
  private removedTrack?: Track;
  private trackElements: Element[] = [];

  constructor(editor: VideoEditor, trackOrId: Track | string) {
    this.editor = editor;
    this.trackId = typeof trackOrId === 'string' ? trackOrId : trackOrId.id;
    // Store the track for undo/redo operations
    this.removedTrack =
      typeof trackOrId === 'string'
        ? this.editor.getTrackById(trackOrId)
        : trackOrId;
  }

  execute(): boolean {
    if (!this.removedTrack) return false;

    // Store all elements in the track for undo operations
    this.trackElements =
      this.editor.timeline.getTrack(this.trackId)?.mutableElements || [];

    const success = this.editor.timeline.removeTrack(this.trackId);
    if (success) {
      this.editor.markDirty('remove_track_command');
      this.editor.notifyObservers(EditorChangeType.TRACK_REMOVED, {
        trackId: this.trackId,
        track: this.removedTrack,
      });
    }
    return success;
  }

  undo(): boolean {
    if (!this.removedTrack) return false;

    // Re-add the track
    const success = this.editor.timeline.addTrack(this.removedTrack);
    if (success) {
      // Re-add all elements that were in the track
      for (const element of this.trackElements) {
        this.editor.timeline.addElement(this.trackId, element);
      }

      this.editor.markDirty('remove_track_command_undo');
      this.editor.notifyObservers(EditorChangeType.TRACK_ADDED, {
        track: this.removedTrack,
      });

      // Notify about restored elements
      for (const element of this.trackElements) {
        this.editor.notifyObservers(EditorChangeType.ELEMENT_ADDED, {
          element: element,
          trackId: this.trackId,
        });
      }
    }
    return !!success;
  }

  redo(): boolean {
    return this.execute();
  }

  getDescription(): string {
    return `Remove track ${this.trackId}`;
  }
}
